import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform, inject } from "@angular/core";
import { Store } from "@ngxs/store";
import { Subscription } from "rxjs";
import { Theme } from "../state/ui.actions";
import { UIState } from "../state/ui.state";

export type ThemeVariant<T> = { [K in Theme]: T } | ({ [K in Theme]?: T } & { "*": T });

/**
 * This pipe is used to provide different values to component inputs based on the current theme.
 * Usage:
 * <custom-component [icon]="{ '*': 'question-mark', 'dark': 'moon', 'light': 'sun' }">
 *   The icon will be sun in light mode and moon in dark mode.
 *   The icon will be question-mark in all other modes.
 * </custom-component>
 *
 * All modes or any subset of modes and a wildcard value must be provided.
 **/
@Pipe({
    name: "themeVariant",
    pure: false,
})
export class ThemeVariantPipe implements PipeTransform, OnDestroy {
    private readonly store = inject(Store);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    private currentTheme: Theme = Theme.Light;
    private subscription: Subscription | null = null;
    private lastValues: ThemeVariant<unknown> | null = null;
    private lastResult: unknown;

    private ensureSubscription(values: ThemeVariant<unknown>) {
        if (!this.subscription) {
            this.currentTheme = this.store.selectSnapshot(UIState.theme);
            this.subscription = this.store.select(UIState.theme).subscribe((theme) => {
                this.currentTheme = theme;
                this.updateResult();
                this.changeDetectorRef.markForCheck();
            });
        }
        this.lastValues = values;
        this.updateResult();
    }

    private updateResult() {
        if (!this.lastValues) {
            return;
        }

        if (this.currentTheme in this.lastValues) {
            this.lastResult = this.lastValues[this.currentTheme as keyof typeof this.lastValues];
        } else {
            this.lastResult = (this.lastValues as { "*": unknown })["*"];
        }
    }

    public transform<T>(values: ThemeVariant<T>): T {
        this.ensureSubscription(values);

        return this.lastResult as T;
    }

    public ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
